import i18n from '@/utils/i18n';
import PencilIcon from '@assets/icons/pencil.svg';
import React, { useState } from 'react';
import { EditCollectionActionSheet } from './edit-collection-action-sheet';
import {
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { useEditOrderGuideState } from '@/contexts/edit-order-guide-context';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';
import { useTheme } from '@/contexts/theme-context';

interface Props {
  canUpdateCollection: boolean;
  collectionNames: string[];
  deleteCollectionMode: 'delete' | 'reset' | null;
  onDelete: () => void;
  style?: StyleProp<ViewStyle>;
}

const TITLE_HIT_SLOP = {
  bottom: 12,
  left: 12,
  right: 12,
  top: 12,
};

export const EditCollectionTitle: React.FC<Props> = ({
  collectionNames,
  canUpdateCollection,
  deleteCollectionMode,
  onDelete,
  style,
}) => {
  const theme = useTheme();
  const { collectionName, collectionUUID } = useEditOrderGuideState();
  const [showListActionSheet, setShowListActionSheet] = useState(false);

  const titleStyle = useSmallLayoutSizeValue(
    TYPOGRAPHY.navigationCenterTitle,
    TYPOGRAPHY.navigationTitleLarge,
  );

  const iconSize = useSmallLayoutSizeValue(14, 18);

  // Don't render title until state is hydrated
  if (!collectionUUID) {
    return null;
  }

  return (
    <>
      <TouchableOpacity
        disabled={!canUpdateCollection}
        hitSlop={TITLE_HIT_SLOP}
        onPress={() => {
          setShowListActionSheet(true);
        }}
      >
        <View style={[styles.base, style]}>
          <Text
            numberOfLines={1}
            style={[
              titleStyle,
              canUpdateCollection ? styles.titleMargin : null,
            ]}
          >
            {collectionName || i18n.t('order_guide')}
          </Text>
          {canUpdateCollection ? (
            <PencilIcon
              fill={theme.colors.inactiveFill}
              height={iconSize}
              style={styles.icon}
              width={iconSize}
            />
          ) : null}
        </View>
      </TouchableOpacity>
      {showListActionSheet && collectionName ? (
        <EditCollectionActionSheet
          collectionNames={collectionNames}
          deleteCollectionMode={deleteCollectionMode}
          initialName={collectionName}
          onClose={() => setShowListActionSheet(false)}
          onDelete={onDelete}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  icon: {
    flexShrink: 0,
  },
  titleMargin: {
    marginRight: 8,
  },
});
